import { configureStore, createListenerMiddleware } from "@reduxjs/toolkit";

// === reducers ===
import userReducer from "../Profile/redux/slices/userSlice";
import medicalReducer from "../Profile/redux/slices/medicalSlice";
import authReducer from "../authPatient/redux/slices/authSlice";
import cycleReducer from "../cycle/redux/slices/cycleSlice";
import medicationReducer, { reminderReceived, clearReminder } from "../medications/redux/slices/medicationSlice";
import medicationsDatabaseReducer from "../medications/redux/slices/medicationsDatabaseSlice";
import placesReducer from "../places/redux/placesSlice";
import emergencyContactReducer from "../Profile/redux/slices/emergencycontactSlice";
import relativesReducer from "../Profile/redux/slices/relativesSlice";
import personalHistoryReducer from "../Profile/redux/slices/personalHistorySlice";
import moderationReducer from "../feed/redux/slices/moderationSlice";
import surgicalHistoryReducer from "../Profile/redux/slices/surgicalHistorySlice";
import familyHistoryReducer from "../Profile/redux/slices/familyHistorySlice";
import bmiHistoryReducer from "../Profile/redux/slices/bmiHistorySlice";
import messagesReducer from "../messagerie/redux/slices/messagesSlice";
import extractionReducer from "../medicalRecord/redux/slices/extractionSlice";
import availabilityReducer from "../appointments/redux/slices/availabilitySlice";
import rendezvousReducer from "../appointments/redux/slices/rendezvousSlice";
import eventsReducer from "../availabilityDoctor/redux/slices/eventsSlice";
import doctorReducer from "../authDoctor/redux/slices/doctorsSlice";
import assistantReducer from "../assistanAi/redux/slices/assistantSlice";
import healthBotReducer, { hbReset } from "../assistanAi/redux/slices/healthBotSlice";
import { hbGetToken, hbRefreshToken } from "../assistanAi/redux/thunks/healthBotThunks";
import consultationReducer from "../consultation/redux/slices/consultationSlice";
import likesReducer from "../feed/redux/slices/likesSlice";
import commentsReducer from "../feed/redux/slices/commentsSlice";
import prescriptionLayoutReducer from "../prescriptions/redux/slices/prescriptionLayoutSlice";
import prescriptionReducer from "../prescriptions/redux/slices/prescriptionSlice";
import postsReducer from "../feed/redux/slices/postsSlice";
import followsReducer from "../ProfileDoctor/redux/slices/followsSlice";
import disponibilityReducer from "../availabilityDoctor/redux/slices/disponibilitySlice";
import { initMedicationSocket } from "../medications/hooks/medicationSocket";
import cycleBotReducer from "../cycle/redux/slices/cycleBotSlice";
import notificationsReducer from "../notifications/redux/notificationsSlice";
import doctorProfileReducer from "../ProfileDoctor/redux/slices/doctorProfileSlice";
import trashReducer from "../medicalRecord/redux/slices/trashSlice"; 
import doctorAssistantReducer from "../assistanAi/redux/slices/doctorAssistantSlice";
import vaccinationReducer from "../Profile/redux/slices/vaccinationSlice";
import vitalReducer from "../Profile/redux/slices/vitalSignalSlice";
import medicalInterractionReducer from "../consultation/redux/slices/medicalInterractionSlice";
import sousProfilesReducer from "../sous-profile/redux/slices/sousProfileSlice";
import adminReducer from "../AdminDashboard/redux/slices/adminSlice";

import roomsReducer from "../WaitingRoom/redux/slices/room.slice";
import localPatientsReducer from "../WaitingRoom/redux/slices/localPatient.slice";

import cnssReducer from "../consultation/redux/slices/cnssSlice";
import cabinetDashboardReducer from "../cabinetDashboard/redux/slices/cabinetDashboardSlice";
import assistantManagementReducer from "../assistantDashboard/redux/slices/assistantManagement.slice";
import medicalCasesReducer from "../Medicalcase/redux/slices/medicalCasesSlice";
import paymentReducer from "../payment/redux/slices/paymentSlice";
import nephrologyReducer from "../specialty/neurologue/redux/slices/nephrologySlice";
import pulmonologyReducer from "../specialty/pulmonology/redux/slices/pulmonologySlice";

/* -----------------------------
 * Listener middlewares
 * ----------------------------- */
const hbListener = createListenerMiddleware();
const debugListener = createListenerMiddleware();

let hbRefreshTimer = null;

const scheduleRefresh = (dispatch, expiresAt) => {
  const delay = Math.max(5_000, (expiresAt ?? 0) - Date.now() - 60_000);
  if (hbRefreshTimer) clearTimeout(hbRefreshTimer);
  hbRefreshTimer = setTimeout(() => dispatch(hbRefreshToken()), delay);
};

// HealthBot refresh
hbListener.startListening({
  actionCreator: hbGetToken.fulfilled,
  effect: async (action, api) => {
    if (action.payload?.expiresAt) scheduleRefresh(api.dispatch, action.payload.expiresAt);
  },
});

hbListener.startListening({
  actionCreator: hbRefreshToken.fulfilled,
  effect: async (action, api) => {
    if (action.payload?.expiresAt) scheduleRefresh(api.dispatch, action.payload.expiresAt);
  },
});

hbListener.startListening({
  actionCreator: hbReset,
  effect: async () => {
    if (hbRefreshTimer) {
      clearTimeout(hbRefreshTimer);
      hbRefreshTimer = null;
    }
  },
});

debugListener.startListening({
  actionCreator: reminderReceived,
  effect: async (action, api) => {
    const st = api.getState();
  },
});

debugListener.startListening({
  actionCreator: clearReminder,
  effect: async (_, api) => {
    const st = api.getState();
  },
});

const store = configureStore({
  reducer: {
    disponibility: disponibilityReducer,
    user: userReducer,
    medical: medicalReducer,
    auth: authReducer,
    cycle: cycleReducer,
    medications: medicationReducer,
    medicationsDatabase: medicationsDatabaseReducer,
    doctorAssistant: doctorAssistantReducer,
    vaccinations: vaccinationReducer,
    vital: vitalReducer,
    places: placesReducer,
    emergencyContacts: emergencyContactReducer,
    relatives: relativesReducer,
    personalHistory: personalHistoryReducer,
    vital: vitalReducer,
    moderation: moderationReducer,
    surgicalHistory: surgicalHistoryReducer,
    familyHistory: familyHistoryReducer,
    bmiHistory: bmiHistoryReducer,
    messages: messagesReducer,
    extraction: extractionReducer,
    availability: availabilityReducer,
    events: eventsReducer,
    rendezvous: rendezvousReducer,
    consultation: consultationReducer,
    likes: likesReducer,
    comments: commentsReducer,
    assistant: assistantReducer,
    healthBot: healthBotReducer,
    doctor: doctorReducer, 
    prescriptionLayout: prescriptionLayoutReducer,
    prescription: prescriptionReducer,
    posts: postsReducer,
    notifications: notificationsReducer,
    follows: followsReducer,
    trash: trashReducer,
    doctorProfile: doctorProfileReducer,
    medicalInterraction: medicalInterractionReducer,
    cycleBot: cycleBotReducer,
    sousProfiles: sousProfilesReducer,
    admin: adminReducer,
    rooms: roomsReducer,
    localPatients: localPatientsReducer,
    cnss: cnssReducer,
    cabinetDashboard: cabinetDashboardReducer,
    assistantManagement: assistantManagementReducer,
    medicalCases: medicalCasesReducer,
    payment: paymentReducer,
    nephrology: nephrologyReducer,
    pulmonology: pulmonologyReducer,

  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(debugListener.middleware)
      .prepend(hbListener.middleware),
});



const sock = initMedicationSocket(store);

export { store };
